import { useState } from 'react';
import { ArrowUpDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { FleetPropertyRow } from '@/hooks/useFleetAnalytics';

interface PropertyPerformanceTableProps {
  properties: FleetPropertyRow[];
  fleetAvgRateCents: number;
}

type SortKey = 'name' | 'pv_kwh' | 'load_kwh' | 'self_consumption_pct' | 'curtailed_kwh' | 'uptime_pct' | 'savings_dollars';

function selfConsumptionColor(pct: number): string {
  if (pct >= 80) return 'text-wt-green-primary';
  if (pct >= 50) return '';
  return 'text-wt-amber-primary';
}

function curtailmentColor(kwh: number): string {
  if (kwh > 100) return 'text-wt-red-primary';
  if (kwh > 25) return 'text-wt-amber-primary';
  return '';
}

function uptimeColor(pct: number): string {
  if (pct >= 100) return 'text-wt-green-primary';
  if (pct >= 95) return '';
  return 'text-wt-red-primary';
}

export function PropertyPerformanceTable({ properties, fleetAvgRateCents }: PropertyPerformanceTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('curtailed_kwh');
  const [sortAsc, setSortAsc] = useState(false);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const sorted = [...properties].sort((a, b) => {
    const av = a[sortKey] ?? '';
    const bv = b[sortKey] ?? '';
    if (av < bv) return sortAsc ? -1 : 1;
    if (av > bv) return sortAsc ? 1 : -1;
    return 0;
  });

  const rateDollars = `$${(fleetAvgRateCents / 100).toFixed(2)}`;

  const th = (label: string, key: SortKey) => (
    <th
      className="px-3 py-2 text-left text-xs font-medium text-muted-foreground cursor-pointer select-none whitespace-nowrap"
      onClick={() => handleSort(key)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${sortKey === key ? 'text-foreground' : ''}`} />
      </span>
    </th>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            {th('Property', 'name')}
            {th('Solar kWh', 'pv_kwh')}
            {th('Load kWh', 'load_kwh')}
            {th('Self-Consumption', 'self_consumption_pct')}
            {th('Curtailed kWh', 'curtailed_kwh')}
            {th('Uptime', 'uptime_pct')}
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Battery</th>
            {th(`Savings (${rateDollars}/kWh)`, 'savings_dollars')}
          </tr>
        </thead>
        <tbody>
          {sorted.map((p) => (
            <tr key={p.id} className="border-b border-border/50 hover:bg-wt-bg-tertiary/50">
              <td className="px-3 py-2">
                <Link to={`/app/dashboard?property=${p.id}`} className="text-wt-green-primary hover:underline">
                  {p.name}
                </Link>
              </td>
              <td className="px-3 py-2 font-mono">{p.pv_kwh.toFixed(1)}</td>
              <td className="px-3 py-2 font-mono">{p.load_kwh.toFixed(1)}</td>
              <td className={`px-3 py-2 font-mono ${selfConsumptionColor(p.self_consumption_pct)}`}>
                {p.self_consumption_pct.toFixed(1)}%
              </td>
              <td className={`px-3 py-2 font-mono ${curtailmentColor(p.curtailed_kwh)}`}>
                {p.curtailed_kwh.toFixed(1)}
              </td>
              <td className={`px-3 py-2 font-mono ${uptimeColor(p.uptime_pct)}`}>
                {p.uptime_pct.toFixed(1)}%
              </td>
              <td className="px-3 py-2 font-mono">
                {p.battery_grade ?? '—'}
              </td>
              <td className="px-3 py-2 font-mono">${p.savings_dollars.toFixed(0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
